import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, EmptyState, SectionCard, Skeleton } from "@/components/Primitives";
import { fmtDate, fmtMoney } from "@/lib/format";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Phone, Mail, Building2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useAttention } from "@/context/AttentionContext";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import BulkActions from "@/components/BulkActions";

const STAGES = ["90 Days Out", "60 Days Out", "30 Days Out", "Contacted", "Renewal Offered", "Renewed", "Lost"];
const daysUntil = (d) => (d ? Math.floor((new Date(d) - Date.now()) / 86400000) : null);

export default function Renewals() {
  const navigate = useNavigate();
  const { refresh: refreshAttention } = useAttention();
  const { user } = useAuth();
  const canDelete = !["sales_rep", "readonly"].includes(user?.role);
  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("duesoon");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/renewals").then((r) => setRows(r.data.items)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStage = async (id, stage) => {
    try { await api.put(`/renewals/${id}`, { stage }); toast.success("Updated"); load(); refreshAttention(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const buckets = useMemo(() => {
    const b = { upcoming: [], duesoon: [], overdue: [], renewed: [], lost: [] };
    rows.forEach((r) => {
      if (r.stage === "Renewed") return b.renewed.push(r);
      if (r.stage === "Lost") return b.lost.push(r);
      const du = daysUntil(r.renewal_date);
      if (du != null && du < 0) b.overdue.push(r);
      else if (du != null && du <= 30) b.duesoon.push(r);
      else b.upcoming.push(r);
    });
    return b;
  }, [rows]);

  const VIEWS = [
    { key: "duesoon", label: "Due Soon" }, { key: "overdue", label: "Overdue" },
    { key: "upcoming", label: "Upcoming" }, { key: "renewed", label: "Renewed" }, { key: "lost", label: "Lost" },
  ];
  const list = buckets[view];

  return (
    <div data-testid="page-renewals">
      <PageHeader title="Renewals" subtitle="Stay ahead of expiring advertiser contracts." />

      <div className="flex flex-wrap gap-2 mb-4">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} data-testid={`renewal-view-${v.key}`}
            className={cn("inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
              view === v.key ? "border-[#00C9E0] bg-[#00C9E0]/10 text-[#0094A8]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}>
            {v.label}<span className={cn("tnum rounded-full px-1.5 text-[11px]", view === v.key ? "bg-[#00C9E0]/20" : "bg-slate-100")}>{buckets[v.key].length}</span>
          </button>
        ))}
      </div>

      {canDelete && list.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <Checkbox checked={list.length > 0 && list.every((r) => sel.has(r.id))} onCheckedChange={(v) => setSel(v ? new Set(list.map((r) => r.id)) : new Set())} data-testid="select-all" />
          <span className="text-sm text-slate-500">Select all in this view</span>
        </div>
      )}
      {canDelete && <BulkActions endpoint="renewals" ids={[...sel]} singular="renewal" title="Renewals" onDone={load} clear={() => setSel(new Set())} />}

      {loading ? <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
        : list.length === 0 ? <SectionCard><EmptyState icon={RefreshCw} title="Nothing here" hint="Renewals appear automatically from contracts with an end date." /></SectionCard>
        : (
          <div className="space-y-3">
            {list.map((r) => {
              const du = daysUntil(r.renewal_date);
              const urgent = du != null && du < 0;
              const soon = du != null && du >= 0 && du <= 30;
              return (
                <div key={r.id} className="cc-card cc-card-hover p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`renewal-${r.id}`}>
                  {canDelete && <div onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`select-${r.id}`} /></div>}
                  <div className="min-w-0 flex-1">
                    <button onClick={() => r.business_id && navigate(`/businesses/${r.business_id}`)} className="font-semibold text-sm text-slate-800 hover:text-[#0094A8] flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-slate-300" />{r.business_name}
                    </button>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1 tnum"><CalendarClock className="h-3 w-3" />Renews {fmtDate(r.renewal_date)}</span>
                      {r.contract_value != null && <span className="tnum">{fmtMoney(r.contract_value)}</span>}
                      {du != null && <span className={cn("font-semibold", urgent ? "text-[#C42607]" : soon ? "text-[#B45309]" : "text-slate-400")}>{urgent ? `${Math.abs(du)}d overdue` : `${du}d left`}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.phone && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" asChild><a href={`tel:${r.phone}`}><Phone className="h-4 w-4" /></a></Button>}
                    {r.email && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" asChild><a href={`mailto:${r.email}`}><Mail className="h-4 w-4" /></a></Button>}
                    <Select value={r.stage} onValueChange={(v) => setStage(r.id, v)}>
                      <SelectTrigger className="w-44 h-8" data-testid={`renewal-stage-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
